import { NgClass, NgIf } from "@angular/common";
import {
  AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnChanges, OnDestroy,
  OnInit, SimpleChanges, ViewChild
} from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ICompany, Module, RequestLicense, RequestLicenseUserSet, RequestModule, RequestRole, Role } from '@myapp/features/admin/models';
import { AdminService } from "@myapp/features/admin/services";
import { AuthPermIdentifier, AuthPermIdentifierParent, TreeInfoAuth, UserSummary } from "@myapp/modules/fwcore/models";
import { RequestUserPermission, RequestUserPermissionSet } from "@myapp/modules/fwcore/models/messages";
import {
  AuthorizationService, ConfirmationDialogHtmlPaneService, ConfirmationDialogService,
  NotificationDialogService, NotificationPopupService
} from "@myapp/modules/fwcore/services";
import { ControlResizerOptions, ControlResizerService, IsgHelperService } from "@mysrc/services/framework";
import { ButtonModule } from "@progress/kendo-angular-buttons";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { AuthTreeComponent } from "../../components/auth/auth-tree/auth-tree.component";
import { ModulesListComponent } from "../../components/modules/modules-list/modules-list.component";
import { RolesListComponent } from "../../components/roles/roles-list/roles-list.component";
import { UserListComponent } from "../../components/users/user-list/user-list.component";

@Component({
  selector: 'app-page-user-permission',
  standalone: true,
  imports: [RouterLink, ButtonModule, UserListComponent, NgClass, ModulesListComponent, NgIf, RolesListComponent, AuthTreeComponent],
  templateUrl: './page-user-permission.component.html',
  styleUrls: ['./page-user-permission.component.scss']
})
export class PageUserPermissionComponent implements AfterViewChecked, OnInit, OnDestroy, AfterViewInit, OnChanges {

  /*-------------------------- public properties / methods --------------------*/

  public currentLicenseId: string | null = null;

  public currentCompanyId: string | null = null;

  public currentUserId: string | null = null;

  public currentUser: UserSummary | null = null;

  public currentModuleId!: string;

  public currentRoleId: string | undefined | null = null;

  public currentRole: Role | null = null;

  public companyName: string | undefined | null = null;

  protected hrefLicenses: string = "./admin/license";
  protected hrefUserManagement: string = "./admin/user/license";
  protected hrefLicenseRoles: string = "./admin/role";
  protected hrefUserPermission: string = "./admin/user/permission";

  /*-------------------------- Internal properties  ---------------------------*/

  @ViewChild(ModulesListComponent)
  protected moduleListComponent!: ModulesListComponent;

  @ViewChild(RolesListComponent)
  protected rolesListComponent!: RolesListComponent;

  @ViewChild('authtreevisual')
  protected authTreeVisualComponent!: AuthTreeComponent;

  @ViewChild('authtreedata')
  protected authTreeDataComponent!: AuthTreeComponent;

  @ViewChild(UserListComponent)
  protected userListComponent!: UserListComponent;

  protected companies: ICompany[] | undefined;

  protected destroy$ = new Subject();

  protected height!: number;

  protected viewInit: boolean = false;

  protected hideModule: boolean = false;

  protected hideAuthDataTree: boolean = false;

  public saveDislayPermissions: boolean | null = null;

  protected enableSavePermissions: boolean = false;

  protected enablePermissionEdits: boolean = false;


  protected reqPerm: RequestUserPermission = {
  }

  protected perms?: AuthPermIdentifier[] = [];


  /*-------------------------- State & permissions  --------------------------*/

  protected showSaveUserRolesButton: boolean = false;
  protected showSaveUserAuthsButton: boolean = false;

  /*-------------------------- Constructor  --------------------------*/
  constructor(protected adminService: AdminService
    , protected authService: AuthorizationService
    , protected notification: NotificationPopupService
    , protected dialogConfirm: ConfirmationDialogService
    , protected dialogNotification: NotificationDialogService
    , protected dialogConfirmPane: ConfirmationDialogHtmlPaneService
    , protected activatedRoute: ActivatedRoute
    , protected router: Router
    , protected controlResizer: ControlResizerService
    , protected cdr: ChangeDetectorRef
    , protected hlp: IsgHelperService
    , protected elem: ElementRef) {

  }

  /*----------------------------------- ng  --------------------------*/

  ngOnInit() {

    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (params) => {
          // check the input id value before assignment to avoid injection
          this.currentLicenseId = this.hlp.getValidGUID(params.get('id'));

          if (!this.currentLicenseId) { this.router.navigate(['./admin/license']); }

          this.updateCompanyInfo();

          this.hrefLicenses = `./admin/license?id=` + this.currentLicenseId;
          this.hrefUserManagement = "./admin/user/license?id=" + this.currentLicenseId;
          this.hrefLicenseRoles = `./admin/role?id=` + this.currentLicenseId;
          this.hrefUserPermission = `./admin/user/permission?id=` + this.currentLicenseId;
        }
      );

    this.adminService.getAllCompanies().pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => { this.companies = resp; }
    );

    this.authService.pagePermissionsGet$(this.reqPerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {
        this.perms = resp.data ?? [];

        this.showSaveUserRolesButton = this.perms?.some(i => i.code === "user-role-set") ?? false;
        this.showSaveUserAuthsButton = this.perms?.some(i => i.code === "user-perm-set") ?? false;


      });

    this.viewInit = true;
    this.sizeChange();
  }

  ngAfterViewChecked(): void {

    this.sizeChange();
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.moduleListComponent.Initialized();
  }

  ngOnDestroy() {

    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes.saveDislayPermissions && !changes.saveDislayPermissions.firstChange) {
      this.getUserAdminPermissions();
    }
  }

  public onPermissionsChanged() {
    this.getUserAdminPermissions();
  }

  /*--------------------------- event handlers  --------------------------*/

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.sizeChange();
  }

  protected onUserSelected(ev: UserSummary | null) {

    this.currentUser = ev;
    this.currentUserId = ev?.id ?? null;

    this.moduleListComponent.checkedIds = [];
    this.moduleListComponent.selectedItem = null;

    this.getUserModules();

    // unselect roles
    this.rolesListComponent.checkedIds = [];
    this.rolesListComponent.clearSelection();

    this.getUserRoles();

    this.getUserAdminPermissions();
  }

  protected onModuleSelected(ev: Module | null) {
    if (ev?.id) {
      this.currentModuleId = ev.id;

      this.userListComponent.Initialized();
    }
  }

  protected onModulesLoaded(count: number | null) {
    let v = !!(count) ? (count < 2) : true;
    this.hideModule = v;
  }

  protected onRoleSelected(role: Role | null) {

    this.currentRole = role;
    this.currentRoleId = role?.id;

    this.authTreeVisualComponent.userFilterId = null;
    this.authTreeDataComponent.userFilterId = null;

    if (this.currentRole) {
      this.authTreeVisualComponent.messageInfo = `Role Permissions for: ${this.currentRole.name}`;
      this.authTreeDataComponent.messageInfo = `Role Permissions for: ${this.currentRole.name}`;
    }
    else {
      this.authTreeVisualComponent.messageInfo = null;
      this.authTreeDataComponent.messageInfo = null;
    }

    this.enableSavePermissions = false;
  }

  protected onDataTreeLoaded(count: number | null) {
    let v = !!(count) ? (count < 1) : true;
    this.hideAuthDataTree = v;
  }

  protected onSaveUserRolesClick(process: number) {

    if (this.userListComponent.checkedIds.length < 1) {
      this.notification.showNotification("Please select users to process by checking their boxes.", "warning");
      return;
    }
    else if (process != 0 && this.rolesListComponent.getCheckedDataItems().filter(x => x.allLicenseUsers != true).length < 1) {
      this.notification.showNotification("Please select roles to process by checking their boxes (excl. All user role types).", "warning");
      return;
    }

    let msg = '';

    if (process == 1) {
      msg = '<p>Click Yes to <u>add</u> role(s): </p><ul><li>'
        + this.rolesListComponent.getCheckedDataItems().filter(x => x.allLicenseUsers != true).map(y => y.name).join('</li><li>')
        + '</li></ul><p>to</p><ul><li>'
        + this.userListComponent.getCheckedNames().join('</li><li>')
        + '</li></ul>';
    }
    else if (process == 0) {
      if (this.rolesListComponent.getCheckedDataItems().filter(x => x.allLicenseUsers != true).length > 0) {
        msg = '<p>Are you sure you want to <u>assign</u> the role(s) below:</p><ul><li>'
          + this.rolesListComponent.getCheckedDataItems().filter(x => x.allLicenseUsers != true).map(y => y.name).join('</li><li>')
          + '</li></ul><p>to the user(s) below? This will replace their roles and <u>remove</u> any that are not in the list above (Roles assigned to all users will still apply).</p><ul><li>'
          + this.userListComponent.getCheckedNames().join('</li><li>')
          + '</li></ul>';
      }
      else {
        msg = '<p>Are you sure you want to <u>remove all</u> the custom roles for:</p><ul><li>'
          + this.userListComponent.getCheckedNames().join('</li><li>')
          + '</li></ul>';
      }
    }
    else if (process == -1) {
      msg = '<p>Click Yes to <u>remove</u> role(s): </p><ul><li>'
        + this.rolesListComponent.getCheckedDataItems().filter(x => x.allLicenseUsers != true).map(y => y.name).join('</li><li>')
        + '</li></ul><p>from</p><ul><li>'
        + this.userListComponent.getCheckedNames().join('</li><li>')
        + '</li></ul>';
    }

    this.dialogConfirmPane.open('Confirm Save', msg, 400)
      .subscribe((result: any) => {
        if (result.value) {

          const req: RequestUserPermissionSet = {
            moduleId: this.currentModuleId
            , licenseId: this.currentLicenseId
            , userIds: this.userListComponent.checkedIds
            , updateRoles: true
            , updatePerms: false
            , roleIds: this.rolesListComponent.checkedIds
            , authTypes: null
            , hierarchyIds: null
            , processFilter: process
          }

          this.setUserPermissions(req);

        }
      });



  }

  protected onUpdateUserLicenseClick(adding: boolean) {

    let items: UserSummary[] | null;

    if (adding)
      items = this.userListComponent.getCheckedItems().filter(x => x.assigned == false);
    else
      items = this.userListComponent.getCheckedItems().filter(x => x.assigned == true);

    if (items.length < 1 || !(items)) {

      this.notification.showNotification("No users have been checked to update.", "warning");
      return;
    }

    let msg = '';

    if (adding) {

      msg = '<p>Click Yes to <u>add</u> the user(s) below to the license: </p><ul><li>'
        + items.map(n => n.displayName).join('</li><li>')
        + '</li></ul>';
    }
    else {
      msg = '<p>Click Yes to <u>remove</u> the user(s) below from the license: </p><ul><li>'
        + items.map(n => n.displayName).join('</li><li>')
        + '</li></ul>';
    }

    this.dialogConfirmPane.open('Confirm License Change', msg, 300)
      .subscribe((result: any) => {
        if (result.value) {

          const req: RequestLicenseUserSet = {
            licenseId: this.currentLicenseId
            , assign: adding
            , data: items!.map(m => m.id!)
          };

          this.updateUserLicense(req);
        }
      });

  }

  /*--------------------------- internal functions  --------------------------*/

  protected sizeChange() {

    if (this.viewInit) {
      var options: ControlResizerOptions = new ControlResizerOptions();
      options.offsetTopHeight = 54;
      options.offsetBottomHeight = 21;

      this.height = this.controlResizer.calculateHeight(options);

      options.offsetTopHeight = 6;
      options.offsetBottomHeight = 29;
      this.sizeChangeSiblings(options);
    }

  }

  protected sizeChangeSiblings(options: ControlResizerOptions) {

    if (this.moduleListComponent && this.moduleListComponent.active)
      this.moduleListComponent.heightChange(options);

    if (this.rolesListComponent)
      this.rolesListComponent.heightChange(options);

    if (this.authTreeVisualComponent)
      this.authTreeVisualComponent.heightChange(options);

    if (this.authTreeDataComponent && this.authTreeDataComponent.active)
      this.authTreeDataComponent.heightChange(options);

    if (this.userListComponent) {

      let offset = 0;

      offset = this.elem.nativeElement?.querySelector('.isg-user-auth-user-wrapper .isg-toolbar')?.getBoundingClientRect().bottom;
      let ch = options.screenHeight - options.headerHeight - options.subMenuHeight - offset - options.footerHeight + 6;

      this.userListComponent.heightControl.set(Math.max(ch, 0));
    }
  }

  protected updateCompanyInfo() {

    let req: RequestLicense = { id: this.currentLicenseId! };

    this.adminService.getLicense$(req).pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => {
        if (resp.data && resp.data.length === 1) {
          this.companyName = resp.data[0].companyName;
          this.currentCompanyId = (resp.data[0].companyId) ? resp.data[0].companyId : null;
        }
        else {
          this.companyName = "";
          this.currentCompanyId = null;
        }
      }
    );
  }

  protected getUserRoles() {

    const req: RequestRole = {
      licenseId: this.currentLicenseId
      , moduleId: this.currentModuleId
      , userFilterId: this.currentUser?.id
    };

    this.adminService.getRole$(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        resp => {
          if (req.userFilterId) {
            this.rolesListComponent.checkedIds = resp.data!.map((x: any) => x.id!);
          }
          else {
            this.rolesListComponent.checkedIds = [];
          }
        }
      );

  }

  protected setUserPermissions(req: RequestUserPermissionSet) {

    this.authService.userPermissionSet$(req)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => {

        }))
      .subscribe((res) => {
        if (res.success) {
          if (req.updateRoles)
            this.dialogNotification.openNotificationDialog('Success', 'Roles saved successfully');
          else
            this.dialogNotification.openNotificationDialog('Success', 'Permissions saved successfully');

          this.userListComponent.checkedIds = [];

        }
        else {
          this.dialogNotification.openNotificationDialog('Error', 'Something went wrong');
        }
      });
  }

  protected getUserModules() {

    const req: RequestModule = {
      licenseId: this.currentLicenseId
      , userFilterId: this.currentUser?.id
      , active: null
      , hasApi: null
      , includeHost: true
    };

    this.adminService.getModules$(req)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        resp => {

          if (req.userFilterId) {
            const ids = resp.data!.map((x: any) => x.id!);
            this.moduleListComponent.checkedIds = ids;
          }
          else {
            this.moduleListComponent.checkedIds = [];
          }
        }
      );
  }

  protected getUserAdminPermissions() {

    if (this.currentUser == null
      || !this.currentUser.assigned) {
      this.refreshUserPermissions(null);

      return;
    }

    const userPerm: RequestUserPermission = {
      userFilterId: this.currentUser?.id
      , moduleId: this.currentModuleId
      , includeExclusions: true
    };

    this.authService.userPermissionAdminGet$(userPerm)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        resp => {
          this.refreshUserPermissions(resp.data);
        }
      );
  }

  protected refreshUserPermissions(items: AuthPermIdentifierParent[] | null | undefined) {

    // need a collection of TreeInfo which should be unique

    let dic = new Map<string, AuthPermIdentifierParent>();
    let setExclParents = new Set<string | null>();
    let setCustomUserParents = new Set<string | null>();

    // the collection returned may have duplicate authorizations, where one may be an exclusion
    // So we create a hash collection based on id then if we get a duplicate id we check so that the exclusion will remain
    if (items) {
      let key: string = "";
      let val: any;

      for (let i = 0; i < items.length; i++) {

        val = items[i];
        key = val.id!;

        if (!dic.has(key)) {

          val.sourceType = "role";
          dic.set(key, val);
        }
        else {
          let tr: any = dic.get(key);

          if (!tr.excluded) {
            if (val.excluded) {
              dic.delete(key);
              val.sourceType = "excl";

              dic.set(key, val);

            }
            else {
              tr.sourceType += val.source ?? "";
            }
          }
        }

        if (val.excluded === true) {
          setExclParents.add(val.parentId);
        }
        else if (val.source === "User") {
          setCustomUserParents.add(val.parentId);
        }
      }
    }

    for (let idParent of setExclParents) {
      this.recursiveChildHasExclusions(idParent, dic);
    }

    for (let idParent of setCustomUserParents) {
      this.recursiveChildHasInclusion(idParent, dic);
    }

    //let trItems: TreeInfoAuth[] = Array.from(dic.values());

    /*
    let pars = new Map();

    let outerMap: Map<string, Map<string, any>> = new Map();

    // Now we have removed duplicates create a list of parent id collections
    if (trItems.length > 0) {
      let key: string = "";
      let val: any;
      let parentKey: string;
      
      for (let i = 0; i < trItems.length; i++) {
        val = trItems[i];
        key = val.id!;
        parentKey = val.parentId ?? "";

        // Declare innerMap outside the if block
        let innerMap: Map<string, any> | null | undefined = null;

        if (!outerMap.has(parentKey)) {
          innerMap = new Map();
          outerMap.set(parentKey, innerMap);
        }

        // Remove the 'let' keyword inside the block
        innerMap = outerMap.get(parentKey);

        innerMap!.set(key, val);
      }

    }

    if (outerMap.size > 0) {

      for (let key of outerMap.keys()) {
        
        // Access the inner map using the outer key
        let innerMap = outerMap.get(key);

        if (innerMap) {
          
          for (let innerValue of innerMap.values()) {
            
            // Check if the 'excluded' property is set to 1
            if (innerValue.excluded === true) {
              let dat = dic.get(key);
              dat.hasChildrenExcluded = true;
            }

            if (innerValue.source === "User") {
              let dat = dic.get(key);
              dat.hasChildrenIncluded = true;
            }
          }
        }
      }
    }
    */

    let parsedItems: TreeInfoAuth[] = Array.from(dic.values());

    this.authTreeVisualComponent.setCheckedSelection(parsedItems);
    this.authTreeVisualComponent.userFilterId = this.currentUser?.id;

    this.authTreeDataComponent.setCheckedSelection(parsedItems);
    this.authTreeDataComponent.userFilterId = this.currentUser?.id;

    if (this.currentUser) {
      this.authTreeVisualComponent.messageInfo = `User permissions for ${this.currentUser.displayName}`;
      this.authTreeDataComponent.messageInfo = `User permissions for ${this.currentUser.displayName}`;
    }
    else {
      this.authTreeVisualComponent.messageInfo = null;
      this.authTreeDataComponent.messageInfo = null;
    }

    this.enableSavePermissions = !!(this.currentUser?.assigned);

  }

  protected recursiveChildHasExclusions(id: string | undefined | null, lookup: Map<string, any>) {
    if (!id)
      return;

    let parent = lookup.get(id);
    if (parent) {
      parent.hasChildExcludedIds = true;

      this.recursiveChildHasExclusions(parent.parentId, lookup);
    }
  }

  protected recursiveChildHasInclusion(id: string | undefined | null, lookup: Map<string, any>) {
    if (!id)
      return;

    let parent = lookup.get(id);
    if (parent) {
      parent.hasChildCustomIds = true;

      this.recursiveChildHasInclusion(parent.parentId, lookup);
    }
  }

  protected updateUserLicense(req: RequestLicenseUserSet) {

    this.authService.licenseUserSet$(req)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => {

        }))
      .subscribe((res) => {
        if (res.success) {
          this.dialogNotification.openNotificationDialog('Success', 'Users updated');

          const checked = this.userListComponent.getCheckedItems();
          checked?.forEach(x => x.assigned = res.assign);

          this.userListComponent.clearCheckedItems();

          if (this.userListComponent.licenseId && this.userListComponent.licenseId.length > 0 && res.assign == false) {
            this.userListComponent.removeItems(checked);
          }
        }
        else {
          this.dialogNotification.openNotificationDialog('Error', 'Something went wrong');
        }
      });
  }

}

